import React, { useCallback, useRef, useMemo } from 'react';
import AiIcon from '@sg/assests/svg/ai-icon.svg';
import DragHandle from '@sg/assests/svg/draghandle-icon.svg';
import DeleteButton from '@sg/components/DeleteButton';
import EditButton from '@sg/components/EditButton';
import cls from 'classnames';
import './index.scss';
import { ContentCardProps } from './types';
import { useIntl } from 'umi';
import COSFileList from '@sg/components/COSFileList';
import UploadFileList from '@sg/components/UploadFileList';
import { renderRichText, renderLiRichText } from '@sg/utils/commonPageMethod';

/**
 * ContentCard component
 *
 * A reusable card that displays a title, optional items (bullet or non-bullet),
 * and a list of file links using COSFileList. Can also render an AI icon variant.
 *
 * @param title - The header text for the card
 * @param titleStyling - Optional custom CSS styles for the title
 * @param type - Component variant (Determines if AI icon should be shown): 'ai' (with AI icon) or 'default'
 * @param items - Content items: Support both array and string
 * @param files - List of file objects with { name, path, format?, size?, url? } - All files must have 'path' property
 * @param variant - Controls item rendering: 'bullet', 'no-bullet', or 'auto' (default to auto)
 * @param fileStyling - Deprecated: No longer used (kept for backward compatibility)
 * @param className - Additional custom CSS class
 * @param renderHtml - If true, renders items as HTML (useful for rich text content from TinyEditor)
 * @param domain - COS domain for file operations (default: 'weekly_project_highlight')
 * @param urlSearchParams - Additional URL search parameters for file operations
 * @param logKeys - Logging keys for file operations
 *
 * @example
 * ```tsx
 * <ContentCard
 *   title="Reports"
 *   type="ai"
 *   items={["Summary", "Details"]}
 *   files={[{ name: "Report.pdf", path: "/files/report.pdf", format: "pdf", size: 1024 }]}
 *   domain="weekly_project_highlight"
 * />
 *
 * // With HTML content
 * <ContentCard
 *   title="Rich Text"
 *   items={["<p><strong>Bold text</strong> and <em>italic text</em></p>"]}
 *   renderHtml={true}
 *   variant="no-bullet"
 * />
 * ```
 */

const ContentCard: React.FC<ContentCardProps> = ({
  title,
  titleStyling,
  type = 'default',
  items,
  files,
  variant = 'auto',
  fileStyling = false,
  className,
  renderHtml = false,
  mode = 'normal',
  onEdit,
  onDelete,
  onFilesChange,
  domain = 'weekly_project_highlight',
  urlSearchParams = {},
  itemId,
  logKeys,
  showFileNamesOnly = false,
  cosConfigDomain,
}) => {
  const { formatMessage } = useIntl();

  // Use ref to prevent infinite loops when handling file changes
  const lastFilesRef = useRef<string>('');

  // Memoize file upload callback to prevent infinite loops
  const handleFilesChange = useCallback((updatedFiles: any[]) => {
    console.log('ContentCard handleFilesChange called with:', updatedFiles);
    // Include both path AND name in the key since name can change independently
    const filesKey = updatedFiles.map(f => `${f.path}|${f.name}`).join(',');
    if (filesKey !== lastFilesRef.current) {
      console.log('✅ Updating files state with:', updatedFiles);
      lastFilesRef.current = filesKey;
      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }
    }
  }, [onFilesChange]);

  // Memoize files to prevent UploadFileList from detecting changes
  const memoizedFiles = useMemo(() => files || [], [
    (files || []).length,
    (files || []).map(f => `${f.path}|${f.name}`).join(',')
  ]);
  // Auto-detect variant based on items type
  const detectedVariant = variant === 'auto'
    ? (Array.isArray(items) ? 'bullet' : 'no-bullet')
    : variant;

  const isBulletVariant = detectedVariant === 'bullet';
  const itemsArray = Array.isArray(items) ? items : [items];

  const titleMap: Record<string, string> = {
    'Meeting Summary': 'dbs_meeting_summary',
    'Summary': 'dbs_summary',
    'Highlights': 'dbs_highlights',
    'Progress Update': 'dbs_progress_update',
    'Next Step': 'dbs_next_step',
  };

  // Check if the title is 'Meeting Summary' and translate it if so.
  const displayTitle = title && titleMap[title]
    ? formatMessage({ id: titleMap[title] })
    : title;

  // 检查 header 是否有内容
  const hasHeaderContent = type === 'ai' || displayTitle || mode === 'edit';

  const content = (
    <div className={cls('dbs-content-card', className)}>
      {hasHeaderContent && (
        <div className="content-card-header-wrapper">
          {type === 'ai' && <img className="content-card-icon" src={AiIcon} alt="AI Icon" />}
          <span className="content-card-title" style={titleStyling}>{displayTitle}</span>

          {/* Action buttons based on mode */}
          {mode === 'edit' && (
            <div className="content-card-actions">
              <EditButton
                // className="content-card-edit-btn"
                onClick={() => onEdit?.()}
                title="Edit"
              />
              <DeleteButton
                // className="content-card-delete-btn"
                onClick={() => onDelete?.()}
                title="Delete"
              />
            </div>
          )}
        </div>
      )}

      {isBulletVariant ? (
        <ul className="content-card-bullet-list">
          {itemsArray.map((item: any, idx: any) => (
            renderHtml ? (
              // <li key={idx} className="content-card-list-item" dangerouslySetInnerHTML={{ __html: item || '' }} />
              renderLiRichText(item, idx, 'content-card-list-item')
            ) : (
              <li key={idx} className="content-card-list-item">{item}</li>
            )
          ))}
        </ul>
      ) : (
        <div className="content-card-no-bullet-content">
          {itemsArray.map((item, idx) => (
            renderHtml ? (
              // <div key={idx} dangerouslySetInnerHTML={{ __html: item || '' }} />
              renderRichText(item)
            ) : (
              <div key={idx}>{item}</div>
            )
          ))}
        </div>
      )}

      {files && files.length > 0 && (
        <>
          {mode === 'edit' ? (
            // Edit mode: Use UploadFileList with rename and delete capabilities (upload button hidden)
            <UploadFileList
              className='dbs-styled-upload-file-list'
              uniqueId={`content-card-${itemId || 'default'}`}
              uploadFolderName={domain}
              defaultList={memoizedFiles as any}
              onResult={handleFilesChange}
              showFileList={true}
              fileListPosition="bottom"
              buttonTips=""
              autoResizeInput={true}
              multiple={true}
              hideUploadButton={true}
            />
          ) : (
            // Normal mode: Use COSFileList for preview，只在需要时禁用操作按钮
            <COSFileList
              className={fileStyling ? 'dbs-styled-cos-file-list' : 'dbs-weekly-files'}
              domain={domain}
              urlSearchParams={itemId ? { ...urlSearchParams, item_id: itemId } : urlSearchParams}
              logKeys={logKeys}
              enableActions={!showFileNamesOnly}
              cosConfigDomain={cosConfigDomain}
              files={files.map(file => {
                // Helper function to split filename and extension
                const splitFileName = (fileName?: string) => {
                  if (!fileName || typeof fileName !== 'string') return { name: '', format: '' };
                  const lastDotIndex = fileName.lastIndexOf('.');
                  if (lastDotIndex <= 0) {
                    return { name: fileName, format: '' };
                  }
                  return {
                    name: fileName.substring(0, lastDotIndex),
                    format: fileName.substring(lastDotIndex) // Includes the dot
                  };
                };

                // Derive a safe file name: prefer provided name, else fallback to basename(path)
                const rawName = (typeof file.name === 'string' && file.name)
                  || (typeof file.path === 'string' && file.path.split('/').pop())
                  || '';

                // Prefer explicit format if provided; otherwise parse from name
                const parsed = file.format ? { name: rawName, format: file.format } : splitFileName(rawName);
                const fileNameWithoutExt = parsed.name || rawName;
                const fileExtension = parsed.format || '';

                // Ensure format doesn't have a dot at the beginning
                const cleanFormat = (fileExtension || '').startsWith('.') ? fileExtension.substring(1) : (fileExtension || '');

                return {
                  ...file,
                  name: fileNameWithoutExt,
                  format: cleanFormat, // Without the dot
                  // Convert size to number if it's a string
                  size: typeof file.size === 'string' ? parseInt(file.size) : file.size || 0,
                };
              })}
            />
          )}
        </>
      )}
    </div>
  );

  // Wrap with drag handle container for edit mode
  if (mode === 'edit') {
    return (
      <div className="dbs-content-card-drag-handle-wrapper">
        {/* Drag handle for edit mode */}
        <div className="dbs-content-card-drag-handle">
          <img src={DragHandle} alt="Drag handle" />
        </div>
        {content}
      </div>
    );
  }

  return content;
};

export default ContentCard;
